import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
    Plane, Users, Plus, Search, Ticket, TrendingUp, CheckCircle,
    XCircle, Clock, Loader2, CalendarDays, ArrowRight
} from 'lucide-react';
import { getLocalToday } from '../utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
    confirmed: { label: 'Confirmada', color: 'bg-emerald-500', icon: CheckCircle },
    pending: { label: 'Pendiente', color: 'bg-amber-500', icon: Clock },
    cancelled: { label: 'Cancelada', color: 'bg-red-500', icon: XCircle },
    checked_in: { label: 'Check-in', color: 'bg-blue-500', icon: Users },
};

export default function VentasPage() {
    const { user, airline } = useAuth();
    const [flights, setFlights] = useState([]);
    const [selectedFlight, setSelectedFlight] = useState(null);
    const [reservations, setReservations] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dateFilter, setDateFilter] = useState(getLocalToday());
    const [showCreate, setShowCreate] = useState(false);
    const [saving, setSaving] = useState(false);
    const [newRes, setNewRes] = useState({
        passenger_name: '', contact_email: '', contact_phone: '', pax_count: 1, notes: '',
        // Auditoria 3 - Item 2: pasajeros individuales, tarifa real y datos comerciales
        passengers: [], fare_amount: '', payment_method: 'card', cabin_class: 'economy', special_requests: '',
    });

    const token = localStorage.getItem('aeroops_token');
    const headers = { Authorization: `Bearer ${token}` };

    const fetchFlights = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API}/flights?date=${dateFilter}`, { headers });
            setFlights(res.data);
        } catch (e) {
            toast.error('Error al cargar vuelos');
        } finally {
            setLoading(false);
        }
    }, [dateFilter]);

    useEffect(() => { fetchFlights(); }, [fetchFlights]);

    const selectFlight = async (flight) => {
        setSelectedFlight(flight);
        try {
            const [resRes, sumRes] = await Promise.all([
                axios.get(`${API}/reservations?flight_id=${flight.id}`, { headers }).catch(() => ({ data: [] })),
                axios.get(`${API}/reservations/flight/${flight.id}/summary`, { headers }).catch(() => ({ data: {} }))
            ]);
            setReservations(resRes.data);
            setSummary(sumRes.data);
        } catch (e) {
            toast.error('Error al cargar reservaciones');
        }
    };

    const createReservation = async () => {
        if (!newRes.passenger_name.trim()) { toast.error('Nombre requerido'); return; }
        setSaving(true);
        try {
            // Auditoria 3 - Item 2: incluir passengers, fare_amount, payment_method, cabin_class, special_requests
            const payload = {
                ...newRes,
                flight_id: selectedFlight.id,
                fare_amount: parseFloat(newRes.fare_amount) || 0,
                passengers: newRes.pax_count > 1 ? (newRes.passengers || []).filter(p => p.first_name || p.document_number) : [],
            };
            await axios.post(`${API}/reservations`, payload, { headers });
            toast.success('Reservacion creada');
            setShowCreate(false);
            setNewRes({ passenger_name: '', contact_email: '', contact_phone: '', pax_count: 1, notes: '', passengers: [], fare_amount: '', payment_method: 'card', cabin_class: 'economy', special_requests: '' });
            selectFlight(selectedFlight);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al crear reservacion');
        } finally {
            setSaving(false);
        }
    };

    // Auditoria 3 - Item 2: gestionar array de pasajeros individuales
    const updatePassengerField = (idx, field, value) => {
        setNewRes(prev => {
            const list = [...(prev.passengers || [])];
            while (list.length <= idx) list.push({ first_name: '', last_name: '', document_number: '' });
            list[idx] = { ...list[idx], [field]: value };
            return { ...prev, passengers: list };
        });
    };
    const trimPassengersToCount = (count) => {
        setNewRes(prev => {
            const list = [...(prev.passengers || [])];
            const target = Math.max(0, (parseInt(count) || 1) - 1); // additional passengers (excluye titular)
            while (list.length < target) list.push({ first_name: '', last_name: '', document_number: '' });
            list.length = target;
            return { ...prev, passengers: list, pax_count: parseInt(count) || 1 };
        });
    };

    const updateStatus = async (resId, status) => {
        try {
            await axios.put(`${API}/reservations/${resId}`, { status }, { headers });
            toast.success('Estado actualizado');
            selectFlight(selectedFlight);
        } catch (e) {
            toast.error('Error al actualizar');
        }
    };

    const cancelReservation = async (resId) => {
        try {
            await axios.delete(`${API}/reservations/${resId}`, { headers });
            toast.success('Reservacion cancelada');
            selectFlight(selectedFlight);
        } catch (e) {
            toast.error('Error al cancelar');
        }
    };

    const filteredFlights = flights.filter(f =>
        f.flight_number?.toLowerCase().includes(search.toLowerCase()) ||
        f.origin?.toLowerCase().includes(search.toLowerCase()) ||
        f.destination?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="space-y-6" data-testid="ventas-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                        <Ticket className="w-6 h-6 text-primary" />Ventas & Reservaciones
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">{airline?.name} - Gestion de reservaciones por vuelo</p>
                </div>
            </div>

            {/* Date and Search */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-muted-foreground" />
                    <Input
                        type="date"
                        value={dateFilter}
                        onChange={e => setDateFilter(e.target.value)}
                        className="w-auto"
                        data-testid="ventas-date-filter"
                    />
                </div>
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Buscar vuelo..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="pl-9"
                        data-testid="ventas-search"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Flights List */}
                <div className="lg:col-span-1 space-y-2">
                    <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Vuelos del dia</h2>
                    {loading ? (
                        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                    ) : filteredFlights.length === 0 ? (
                        <Card><CardContent className="py-8 text-center text-muted-foreground"><Plane className="w-8 h-8 mx-auto mb-2 opacity-40" />No hay vuelos para esta fecha</CardContent></Card>
                    ) : (
                        <div className="space-y-1.5 max-h-[calc(100vh-280px)] overflow-y-auto pr-1">
                            {filteredFlights.map(f => {
                                const reserved = f.pax_reserved || 0;
                                const cap = f.aircraft_capacity || 0;
                                const loadPct = cap > 0 ? Math.round((reserved / cap) * 100) : 0;
                                const isSelected = selectedFlight?.id === f.id;
                                return (
                                    <Card
                                        key={f.id}
                                        className={`cursor-pointer transition-all hover:border-primary/40 ${isSelected ? 'border-primary ring-1 ring-primary/20' : ''}`}
                                        onClick={() => selectFlight(f)}
                                        data-testid={`ventas-flight-${f.id}`}
                                    >
                                        <CardContent className="p-3">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <span className="font-mono font-bold text-sm">{f.flight_number}</span>
                                                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                                                        <span>{f.origin}</span>
                                                        <ArrowRight className="w-3 h-3" />
                                                        <span>{f.destination}</span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className={`text-xs font-mono font-bold ${loadPct >= 80 ? 'text-emerald-500' : loadPct >= 50 ? 'text-amber-500' : 'text-muted-foreground'}`}>
                                                        {reserved}{cap > 0 ? `/${cap}` : ''} PAX
                                                    </div>
                                                    {cap > 0 && (
                                                        <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden mt-1 ml-auto">
                                                            <div className={`h-full rounded-full ${loadPct >= 80 ? 'bg-emerald-500' : loadPct >= 50 ? 'bg-amber-500' : 'bg-blue-400'}`} style={{ width: `${Math.min(loadPct, 100)}%` }} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Reservations Panel */}
                <div className="lg:col-span-2">
                    {!selectedFlight ? (
                        <Card>
                            <CardContent className="py-16 text-center text-muted-foreground">
                                <Ticket className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p>Selecciona un vuelo para ver y gestionar reservaciones</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-4">
                            {/* Flight Header + Summary */}
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                        <div>
                                            <h2 className="text-lg font-bold font-mono flex items-center gap-2">
                                                <Plane className="w-5 h-5 text-primary" />
                                                {selectedFlight.flight_number}
                                                <span className="text-sm font-normal text-muted-foreground">
                                                    {selectedFlight.origin} <ArrowRight className="w-3 h-3 inline" /> {selectedFlight.destination}
                                                </span>
                                            </h2>
                                        </div>
                                        <Button onClick={() => setShowCreate(true)} data-testid="create-reservation-btn">
                                            <Plus className="w-4 h-4 mr-1" />Nueva Reservacion
                                        </Button>
                                    </div>
                                    {/* Summary Cards */}
                                    {summary && (
                                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
                                            <div className="p-2.5 rounded-lg bg-muted/30 text-center">
                                                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Capacidad</p>
                                                <p className="text-lg font-bold font-mono">{summary.aircraft_capacity || '—'}</p>
                                            </div>
                                            <div className="p-2.5 rounded-lg bg-emerald-500/10 text-center">
                                                <p className="text-[10px] uppercase tracking-widest text-emerald-500">Reservados</p>
                                                <p className="text-lg font-bold font-mono text-emerald-500">{summary.pax_reserved}</p>
                                            </div>
                                            <div className="p-2.5 rounded-lg bg-amber-500/10 text-center">
                                                <p className="text-[10px] uppercase tracking-widest text-amber-500">Pendientes</p>
                                                <p className="text-lg font-bold font-mono text-amber-500">{summary.pending}</p>
                                            </div>
                                            <div className="p-2.5 rounded-lg bg-red-500/10 text-center">
                                                <p className="text-[10px] uppercase tracking-widest text-red-500">Canceladas</p>
                                                <p className="text-lg font-bold font-mono text-red-500">{summary.cancelled}</p>
                                            </div>
                                            <div className="p-2.5 rounded-lg bg-primary/10 text-center">
                                                <p className="text-[10px] uppercase tracking-widest text-primary">Ocupacion Est.</p>
                                                <p className="text-lg font-bold font-mono text-primary">{summary.load_pct}%</p>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Reservations Table */}
                            <Card>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm flex items-center gap-2">
                                        <Users className="w-4 h-4" />Reservaciones ({reservations.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {reservations.length === 0 ? (
                                        <div className="py-8 text-center text-muted-foreground text-sm">
                                            No hay reservaciones para este vuelo
                                        </div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm" data-testid="reservations-table">
                                                <thead>
                                                    <tr className="border-b text-left text-xs text-muted-foreground uppercase">
                                                        <th className="py-2 pr-3">PNR</th>
                                                        <th className="py-2 pr-3">Pasajero</th>
                                                        <th className="py-2 pr-3">PAX</th>
                                                        <th className="py-2 pr-3">Contacto</th>
                                                        <th className="py-2 pr-3">Estado</th>
                                                        <th className="py-2">Acciones</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reservations.map(r => {
                                                        const sc = statusConfig[r.status] || statusConfig.pending;
                                                        const StatusIcon = sc.icon;
                                                        return (
                                                            <tr key={r.id} className="border-b border-border/30 hover:bg-muted/20" data-testid={`res-row-${r.id}`}>
                                                                <td className="py-2.5 pr-3 font-mono text-xs">{r.pnr}</td>
                                                                <td className="py-2.5 pr-3 font-medium">{r.passenger_name}</td>
                                                                <td className="py-2.5 pr-3 font-mono">{r.pax_count}</td>
                                                                <td className="py-2.5 pr-3 text-xs text-muted-foreground">
                                                                    {r.contact_email && <div>{r.contact_email}</div>}
                                                                    {r.contact_phone && <div>{r.contact_phone}</div>}
                                                                </td>
                                                                <td className="py-2.5 pr-3">
                                                                    <Badge className={`${sc.color} text-white text-[10px] gap-1`}>
                                                                        <StatusIcon className="w-3 h-3" />{sc.label}
                                                                    </Badge>
                                                                </td>
                                                                <td className="py-2.5">
                                                                    <div className="flex items-center gap-1">
                                                                        {r.status === 'pending' && (
                                                                            <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-500" onClick={() => updateStatus(r.id, 'confirmed')} data-testid={`confirm-res-${r.id}`}>
                                                                                Confirmar
                                                                            </Button>
                                                                        )}
                                                                        {r.status !== 'cancelled' && r.status !== 'checked_in' && (
                                                                            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => cancelReservation(r.id)} data-testid={`cancel-res-${r.id}`}>
                                                                                Cancelar
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Reservation Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent data-testid="create-reservation-dialog">
                    <DialogHeader>
                        <DialogTitle>Nueva Reservacion - {selectedFlight?.flight_number}</DialogTitle>
                        <DialogDescription>
                            {selectedFlight?.origin} → {selectedFlight?.destination} | Capacidad: {selectedFlight?.aircraft_capacity || '—'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Nombre del pasajero *</Label>
                            <Input value={newRes.passenger_name} onChange={e => setNewRes({...newRes, passenger_name: e.target.value})} placeholder="Nombre completo" data-testid="res-passenger-name" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Email</Label>
                                <Input type="email" value={newRes.contact_email} onChange={e => setNewRes({...newRes, contact_email: e.target.value})} placeholder="email@ejemplo.com" data-testid="res-email" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Telefono</Label>
                                <Input value={newRes.contact_phone} onChange={e => setNewRes({...newRes, contact_phone: e.target.value})} placeholder="+52 ..." data-testid="res-phone" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label># Pasajeros</Label>
                            <Input type="number" min="1" max="20" value={newRes.pax_count} onChange={e => trimPassengersToCount(e.target.value)} data-testid="res-pax-count" />
                        </div>
                        {/* Auditoria 3 - Item 2: lista de pasajeros individuales cuando pax_count > 1 */}
                        {newRes.pax_count > 1 && (
                            <div className="space-y-2" data-testid="res-extra-passengers">
                                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Pasajeros adicionales ({newRes.pax_count - 1})</Label>
                                {Array.from({ length: newRes.pax_count - 1 }).map((_, i) => (
                                    <div key={i} className="grid grid-cols-3 gap-2 p-2 rounded bg-muted/20">
                                        <Input placeholder="Nombre" value={(newRes.passengers?.[i]?.first_name) || ''} onChange={e => updatePassengerField(i, 'first_name', e.target.value)} data-testid={`res-extra-first-${i}`} />
                                        <Input placeholder="Apellido" value={(newRes.passengers?.[i]?.last_name) || ''} onChange={e => updatePassengerField(i, 'last_name', e.target.value)} data-testid={`res-extra-last-${i}`} />
                                        <Input placeholder="Documento" value={(newRes.passengers?.[i]?.document_number) || ''} onChange={e => updatePassengerField(i, 'document_number', e.target.value)} data-testid={`res-extra-doc-${i}`} />
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Tarifa pagada (USD)</Label>
                                <Input type="number" min="0" step="0.01" value={newRes.fare_amount} onChange={e => setNewRes({...newRes, fare_amount: e.target.value})} placeholder="150.00" data-testid="res-fare-amount" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Método de pago</Label>
                                <Select value={newRes.payment_method} onValueChange={v => setNewRes({...newRes, payment_method: v})}>
                                    <SelectTrigger data-testid="res-payment-method"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="cash">Efectivo</SelectItem>
                                        <SelectItem value="card">Tarjeta</SelectItem>
                                        <SelectItem value="transfer">Transferencia</SelectItem>
                                        <SelectItem value="other">Otro</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Clase de cabina</Label>
                            <Select value={newRes.cabin_class} onValueChange={v => setNewRes({...newRes, cabin_class: v})}>
                                <SelectTrigger data-testid="res-cabin-class"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="economy">Economy</SelectItem>
                                    <SelectItem value="business">Business</SelectItem>
                                    <SelectItem value="first">First</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Requerimientos especiales</Label>
                            <Textarea value={newRes.special_requests} onChange={e => setNewRes({...newRes, special_requests: e.target.value})} placeholder="Silla de ruedas, comida especial, menor sin acompanante..." rows={2} data-testid="res-special-requests" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Notas</Label>
                            <Textarea value={newRes.notes} onChange={e => setNewRes({...newRes, notes: e.target.value})} placeholder="Notas adicionales..." rows={2} data-testid="res-notes" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                        <Button onClick={createReservation} disabled={saving} data-testid="save-reservation-btn">
                            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Crear Reservacion
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
